import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import { FormFooter } from '@/components/basicComponents/formFooter';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Textarea from '@/components/basicComponents/textarea';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { useCustomizationsData } from '@/hooks/useCustomizationsData';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTheme } from 'styled-components';
import { SettingsContainer, SettingsSection } from '../..';
import { IFirmSettings } from '../templatesSettings/types';

const maximumCharacters: { [key: string]: number } = {
  pageTitle: 20,
  bannerImageText: 200,
  headline: 100
};

interface CatalogBannerTextProps {
  currentRow: IFirmSettings | null;
  handleClose: () => void;
}

export const CatalogBannerText = ({ currentRow, handleClose }: CatalogBannerTextProps) => {
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { updateCustomizationsDataByKey } = useCustomizationsData();
  const { control, reset, handleSubmit } = useForm<{ text: string }>({ defaultValues: { text: '' } });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!currentRow) return;
    reset({ text: currentRow?.value ?? '' });
  }, [currentRow]);

  const handleUpdateSetting = async (id: string, value: string) => {
    try {
      setLoading(true);

      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/system/update-customization`,
        {
          key: 'theme',
          value: JSON.stringify({
            ...theme,
            catalogBanner: {
              ...theme.catalogBanner,
              [currentRow?.key]: value
            }
          })
        },
        {
          headers: { authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      if (data?.value) {
        updateCustomizationsDataByKey('theme', JSON.parse(data.value));
      }
      handleClose();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(true);
    }
  };

  const onSubmit = (data: { text: string }) => {
    const { text } = data;
    setErrorMessage(null);

    handleUpdateSetting(currentRow?.key, text);
  };

  if (!currentRow) return <></>;

  return (
    <>
      {loading && (
        <ModalWrappedContent
          isOpen={loading}
          title="Saving"
          showCancelButton={false}
          showCloseIcon={false}
          isUseOutsideClick={false}
          onClose={() => {}}
        >
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleClose} backToTitle="Firm Settings" />} />
        <PageTitle title="Text" />
      </MainWrap>
      <MainWrap>
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <SettingsContainer>
          <SettingsSection isMobile={isMobile}>
            <Controller
              name="text"
              control={control}
              rules={{
                required: {
                  value: currentRow.key === 'pageTitle',
                  message: `${currentRow.fieldName} is required`
                }
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Textarea
                  label={currentRow?.fieldName ?? ''}
                  value={value}
                  onChange={onChange}
                  errorText={error?.message}
                  size="md"
                  isRequiredField={currentRow.key === 'pageTitle'}
                  autoFocus
                  {...(maximumCharacters[currentRow?.key] && {
                    limitCharactersTo: maximumCharacters[currentRow?.key]
                  })}
                />
              )}
            />
          </SettingsSection>
          <FormFooter onCancel={handleClose} onSave={handleSubmit(onSubmit)} disableSaveButton={loading} showRequiredFieldsWarning={false} />
        </SettingsContainer>
      </MainWrap>
    </>
  );
};
